/**
 * Trial Session Service
 *
 * Handles database persistence for trial sessions.
 * Trial data is synced to the database so it's not lost when localStorage is cleared.
 */

import { supabase } from '@/lib/supabase'
import { Phase1Data, Phase2Data, Phase3Data } from '@/types'

export interface TrialSessionDB {
  id: string
  session_id: string
  created_at: string
  updated_at: string
  expires_at: string
  current_phase: number
  phase1_data: Phase1Data | null
  phase2_data: Phase2Data | null
  phase3_data: Phase3Data | null
  user_id: string | null
  converted_at: string | null
  converted_project_id: string | null
}

// Error codes for specific trial session states
export type TrialSessionError =
  | 'SESSION_NOT_FOUND'
  | 'SESSION_EXPIRED'
  | 'SESSION_CONVERTED'
  | 'FETCH_ERROR'

export interface TrialSessionResult {
  success: boolean
  data?: TrialSessionDB
  error?: TrialSessionError
  convertedProjectId?: string // If already converted, include the project ID
}

/**
 * Create or update a trial session in the database
 * This is called in the background to sync localStorage data to DB
 */
export async function syncTrialSessionToDB(
  sessionId: string,
  data: {
    currentPhase: number
    phase1Data?: Phase1Data
    phase2Data?: Phase2Data
    phase3Data?: Phase3Data
    createdAt?: string
  }
): Promise<{ success: boolean; error?: string }> {
  try {
    const { error } = await supabase
      .from('trial_sessions')
      .upsert(
        {
          session_id: sessionId,
          current_phase: data.currentPhase,
          phase1_data: data.phase1Data || {},
          phase2_data: data.phase2Data || { pages: [] },
          phase3_data: data.phase3Data || {},
          updated_at: new Date().toISOString(),
          // Only set created_at on first insert (upsert handles this via conflict)
        },
        {
          onConflict: 'session_id',
          ignoreDuplicates: false,
        }
      )

    if (error) {
      console.error('[trialSessionService] Error syncing to DB:', error)
      return { success: false, error: error.message }
    }

    return { success: true }
  } catch (err) {
    console.error('[trialSessionService] Unexpected error:', err)
    return { success: false, error: 'Unexpected error syncing session' }
  }
}

/**
 * Get a trial session from the database
 * Used as fallback when localStorage data is missing
 * Returns specific error codes for better UX
 */
export async function getTrialSessionFromDB(
  sessionId: string
): Promise<TrialSessionResult> {
  try {
    const { data, error } = await supabase
      .from('trial_sessions')
      .select('*')
      .eq('session_id', sessionId)
      .single()

    if (error) {
      // PGRST116 = not found, which is expected for new sessions
      if (error.code === 'PGRST116') {
        return { success: false, error: 'SESSION_NOT_FOUND' }
      }
      console.error('[trialSessionService] Error fetching from DB:', error)
      return { success: false, error: 'FETCH_ERROR' }
    }

    // Check if session is expired
    if (data.expires_at && new Date(data.expires_at) < new Date()) {
      return { success: false, error: 'SESSION_EXPIRED' }
    }

    // Check if already converted
    if (data.converted_at) {
      return {
        success: false,
        error: 'SESSION_CONVERTED',
        convertedProjectId: data.converted_project_id || undefined,
      }
    }

    return { success: true, data }
  } catch (err) {
    console.error('[trialSessionService] Unexpected error:', err)
    return { success: false, error: 'FETCH_ERROR' }
  }
}

/**
 * Get a trial session from the database (legacy version returning null)
 * @deprecated Use getTrialSessionFromDB instead for better error handling
 */
export async function getTrialSessionFromDBLegacy(
  sessionId: string
): Promise<TrialSessionDB | null> {
  const result = await getTrialSessionFromDB(sessionId)
  return result.success ? result.data! : null
}

/**
 * Mark a trial session as converted (linked to a real project)
 */
export async function markTrialSessionConverted(
  sessionId: string,
  userId: string,
  projectId: string
): Promise<{ success: boolean; error?: string }> {
  try {
    const { error } = await supabase
      .from('trial_sessions')
      .update({
        user_id: userId,
        converted_at: new Date().toISOString(),
        converted_project_id: projectId,
      })
      .eq('session_id', sessionId)

    if (error) {
      console.error('[trialSessionService] Error marking converted:', error)
      return { success: false, error: error.message }
    }

    return { success: true }
  } catch (err) {
    console.error('[trialSessionService] Unexpected error:', err)
    return { success: false, error: 'Unexpected error marking session converted' }
  }
}

/**
 * Delete a trial session from the database
 */
export async function deleteTrialSessionFromDB(
  sessionId: string
): Promise<{ success: boolean; error?: string }> {
  try {
    const { error } = await supabase
      .from('trial_sessions')
      .delete()
      .eq('session_id', sessionId)

    if (error) {
      console.error('[trialSessionService] Error deleting from DB:', error)
      return { success: false, error: error.message }
    }

    return { success: true }
  } catch (err) {
    console.error('[trialSessionService] Unexpected error:', err)
    return { success: false, error: 'Unexpected error deleting session' }
  }
}
